import React, { MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CoreLink,
  RecursiveMenu,
  Webform,
} from '@uncinc/react-drupal-core';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import { useSelector } from 'react-redux';
import { MenuItemLinkElementProps, MenuItemLinkProps, MenuItemTitleProps } from '@uncinc/react-drupal-core/dist/Components/RecursiveMenu/types';
import useCookie from '../../../helpers/useCookie';
import { SocialMenu, WebformNewsletterAction } from '../../../components';
import { ReactComponent as UncIncLogo } from '../../../assets/images/UncIncLogo.svg';
import { ReactComponent as BCorpLogo } from '../../../assets/images/BCorpLogo.svg';
import { ReactComponent as ISO9001Logo } from '../../../assets/images/ISO9001Logo.svg';
import { ReactComponent as ISO27001Logo } from '../../../assets/images/ISO27001Logo.svg';
import { ReactComponent as DDALogo } from '../../../assets/images/DDALogo.svg';

import './style.scss';

const FooterMenu = () => {
  const { t } = useTranslation();
  const { updateConsent } = useCookie();
  const lang = useSelector<Immutable.Map<any, any>>((state) => state.getIn(['ui', 'language']));
  const about = lang === 'nl' ? 'over-ons' : 'about-us';
  const menuIdExpertise = 'expertise';
  const menuIdFooter = 'footer';
  const menuIdContact = 'contact';
  const menuIdLegal = 'legal';

  // Handle cookie consent through menu link with cookie-consent class
  // instead of a button like we had before as design is different now
  // this is the only way to do this, A11Y still works
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target) {
      const parent = (event.target as HTMLDivElement).closest('.cookie-consent');
      if (parent) {
        event.preventDefault();
        updateConsent();
      }
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    const footerEl = event.currentTarget.closest('footer');
    if (!footerEl || getComputedStyle(footerEl).position !== 'sticky') return;

    const isAtBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 1;
    if (isAtBottom) return;

    window.scrollTo({ top: document.body.scrollHeight });
  };

  return (
    <div className="FooterMenu" onFocus={handleFocus}>
      <Container>
        <div className="FooterMenu__wrapper">
          <div className="FooterMenu__wrapper__columns">
            <UncIncLogo className="UncIncLogo" />
            <div className="FooterMenu__newsletter">
              <h3>{t('footer.newsletter')}</h3>
              <Webform validateOnSubmitOnly addFocusOnFirstInvalidField id="newsletter" overriddenComponents={{ webform_actions: WebformNewsletterAction }} />
            </div>
            <div className="FooterMenu__menus">
              <RecursiveMenu
                menuId={menuIdExpertise}
                maxLevel={2}
                navProps={{ 'aria-label': t(`menu_label.${menuIdExpertise}`) }}
              />
              <RecursiveMenu
                menuId={menuIdFooter}
                maxLevel={2}
                navProps={{ 'aria-label': t(`menu_label.${menuIdFooter}`) }}
              />
              <RecursiveMenu
                menuId={menuIdContact}
                maxLevel={2}
                navProps={{ 'aria-label': t(`menu_label.${menuIdContact}`) }}
              />
            </div>
          </div>
        </div>

        <div className="FooterMenu__certificates">
          <CoreLink
            ariaLabel={t('certificates.iso_9001')}
            title="iso9001"
            href={`/${lang}/${about}/iso-9001`}
          >
            <ISO9001Logo className="Certification" />
          </CoreLink>
          <CoreLink
            ariaLabel={t('certificates.dutch_digital_agencies')}
            title="dda"
            href={`/${lang}/${about}/dda`}
          >
            <DDALogo className="Certification" />
          </CoreLink>
          <CoreLink
            ariaLabel={t('certificates.b_corp')}
            title="b-corp"
            href={`/${lang}/${about}/b-corp`}
          >
            <BCorpLogo className="Certification" />
          </CoreLink>
          <CoreLink
            ariaLabel={t('certificates.iso_27001')}
            title="dutch digital agencies"
            href={`/${lang}/${about}/iso-27001`}
          >
            <ISO27001Logo className="Certification" />
          </CoreLink>
        </div>

        <div className="FooterMenu__bottom">
          <div className="FooterMenu__legal-group" onClickCapture={handleClick}>
            <RecursiveMenu navProps={{ 'aria-label': t(`menu_label.${menuIdLegal}`) }} menuId={menuIdLegal} maxLevel={1} />
          </div>
          <SocialMenu colorClass="white" showHeader={false} />
        </div>
      </Container>
    </div>
  );
};

export default FooterMenu;
